import { subscribePageForeground } from './camera-lifecycle';
import { isMobilePhoneUserAgent, VOLC_DEGRADED_VIDEO_ENCODER_CONFIG, VOLC_VIDEO_ENCODER_CONFIG } from './engine';
import { playWithWeChatUnlock } from './wechat-media-unlock';

const CAMERA_PREVIEW_TIMEOUT_MS = 3_000;
const DEFAULT_MIRROR_FRAME_RATE = 24;

interface MirrorPlaybackControls {
  pause: () => void;
  resume: () => void;
  stopSource: () => void;
}

const mirrorPlaybackByStream = new WeakMap<MediaStream, MirrorPlaybackControls>();

export const pauseMirroredCameraPlayback = (stream: MediaStream | null | undefined): void => {
  if (!stream) {
    return;
  }
  mirrorPlaybackByStream.get(stream)?.pause();
};

export const resumeMirroredCameraPlayback = (stream: MediaStream | null | undefined): void => {
  if (!stream) {
    return;
  }
  mirrorPlaybackByStream.get(stream)?.resume();
};

export const hasMirroredCameraPlayback = (stream: MediaStream | null | undefined): boolean => {
  return Boolean(stream && mirrorPlaybackByStream.has(stream));
};

/** 停掉隐藏 GUM 源轨，保留 canvas 最后一帧。采麦时必须把摄像头设备真正关掉，pause 不够。 */
export const stopMirroredCameraSource = (stream: MediaStream | null | undefined): boolean => {
  if (!stream) {
    return false;
  }
  const controls = mirrorPlaybackByStream.get(stream);
  if (!controls) {
    return false;
  }
  controls.stopSource();
  return true;
};

export interface MirrorCameraMaxSize {
  maxHeight: number;
  maxWidth: number;
  targetFrameRate?: number;
}

const toEven = (value: number): number => Math.max(2, Math.round(value / 2) * 2);

/** 手机上行锁 270p，桌面 / iPad 锁 540p，和会话层编码器上限一致。 */
export const resolveMirrorCameraMaxSize = (
  userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  allowMobileHighUplink = false,
): MirrorCameraMaxSize => {
  const config =
    isMobilePhoneUserAgent(userAgent) && !allowMobileHighUplink
      ? VOLC_DEGRADED_VIDEO_ENCODER_CONFIG
      : VOLC_VIDEO_ENCODER_CONFIG;
  return {
    maxHeight: config.height.max,
    maxWidth: config.width.max,
  };
};

/**
 * 按编码上限等比缩小，不放大、不拉变形。
 * 竖屏前置如果直接画成 960×540，会把人拉成横屏。
 */
export const fitMirrorCanvasSize = (
  sourceWidth: number,
  sourceHeight: number,
  maxWidth: number,
  maxHeight: number,
): { height: number; width: number } => {
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return { height: sourceHeight, width: sourceWidth };
  }
  const scale = Math.min(1, maxWidth / sourceWidth, maxHeight / sourceHeight);
  if (scale === 1) {
    return { height: sourceHeight, width: sourceWidth };
  }
  return {
    height: toEven(sourceHeight * scale),
    width: toEven(sourceWidth * scale),
  };
};

const getCameraVideoTrack = (stream: MediaStream): MediaStreamTrack | undefined => stream.getVideoTracks?.()[0];

const cleanupPreviewVideo = (video: HTMLVideoElement): void => {
  try {
    video.pause();
  } catch {
    // jsdom 和部分 WebView 的 pause 会抛未实现错误。
  }
  video.srcObject = null;
  video.remove();
};

const waitForVideoDimensions = (video: HTMLVideoElement): Promise<void> => {
  if (video.videoWidth > 0 && video.videoHeight > 0) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      finish();
      reject(new Error('camera_preview_timeout'));
    }, CAMERA_PREVIEW_TIMEOUT_MS);

    const finish = () => {
      window.clearTimeout(timeoutId);
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('playing', onReady);
    };

    const onReady = () => {
      if (video.videoWidth <= 0 || video.videoHeight <= 0) {
        return;
      }
      finish();
      resolve();
    };

    video.addEventListener('loadeddata', onReady);
    video.addEventListener('playing', onReady);
  });
};

const drawMirroredFrame = (
  context: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  width: number,
  height: number,
): void => {
  context.save();
  context.translate(width, 0);
  context.scale(-1, 1);
  context.drawImage(video, 0, 0, width, height);
  context.restore();
};

/**
 * 把摄像头画面水平翻转后再交给预览和 RTC 上行。
 * 火山 setLocalVideoMirrorType 只翻 SDK 播放器，编码传输不会翻。
 * 后置摄像头保持原样；无法创建 canvas 流时回退原流，避免开不了摄像头。
 */
export const createMirroredCameraStream = async (
  source: MediaStream,
  maxSize: MirrorCameraMaxSize = resolveMirrorCameraMaxSize(),
): Promise<MediaStream> => {
  const sourceTrack = getCameraVideoTrack(source);
  if (!sourceTrack || sourceTrack.getSettings?.()?.facingMode === 'environment') {
    return source;
  }

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', 'true');
  video.srcObject = source;

  try {
    await playWithWeChatUnlock(() => video.play());
    await waitForVideoDimensions(video);
  } catch {
    cleanupPreviewVideo(video);
    return source;
  }

  const { height, width } = fitMirrorCanvasSize(
    video.videoWidth,
    video.videoHeight,
    maxSize.maxWidth,
    maxSize.maxHeight,
  );
  const frameRate = maxSize.targetFrameRate ?? (sourceTrack.getSettings?.().frameRate || DEFAULT_MIRROR_FRAME_RATE);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  const output = typeof canvas.captureStream === 'function' ? canvas.captureStream(frameRate) : null;
  const outputTrack = output?.getVideoTracks()[0];
  if (!context || !output || !outputTrack) {
    cleanupPreviewVideo(video);
    return source;
  }

  drawMirroredFrame(context, video, width, height);

  const videoWithFrameCallback = video as HTMLVideoElement & {
    cancelVideoFrameCallback?: (handle: number) => void;
    requestVideoFrameCallback?: (callback: () => void) => number;
  };
  let frameHandle = 0;
  let usesVideoFrameCallback = false;
  const tick = () => {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      drawMirroredFrame(context, video, width, height);
    }
    if (maxSize.targetFrameRate) {
      frameHandle = window.setTimeout(tick, 1000 / frameRate);
      return;
    }
    if (typeof videoWithFrameCallback.requestVideoFrameCallback === 'function') {
      usesVideoFrameCallback = true;
      frameHandle = videoWithFrameCallback.requestVideoFrameCallback(tick);
      return;
    }
    usesVideoFrameCallback = false;
    frameHandle = requestAnimationFrame(tick);
  };
  tick();

  const stopTick = () => {
    if (maxSize.targetFrameRate) {
      window.clearTimeout(frameHandle);
      return;
    }
    if (usesVideoFrameCallback && typeof videoWithFrameCallback.cancelVideoFrameCallback === 'function') {
      videoWithFrameCallback.cancelVideoFrameCallback(frameHandle);
      return;
    }
    cancelAnimationFrame(frameHandle);
  };

  let stopped = false;
  let pausedForAudioCapture = false;
  let sourceStopped = false;
  const resumeMirror = () => {
    if (stopped || pausedForAudioCapture || sourceStopped) {
      return;
    }
    void playWithWeChatUnlock(() => video.play()).then(
      () => {
        if (stopped) {
          return;
        }
        stopTick();
        tick();
      },
      () => {
        // 回前台 play 失败时保留上一帧循环，避免把整条镜像流拆掉。
      },
    );
  };

  const unsubscribeForeground = subscribePageForeground(resumeMirror);
  sourceTrack.addEventListener('unmute', resumeMirror);

  const originalStop = outputTrack.stop.bind(outputTrack);
  const stopMirroredStream = () => {
    if (stopped) {
      return;
    }
    stopped = true;
    unsubscribeForeground();
    sourceTrack.removeEventListener('unmute', resumeMirror);
    stopTick();
    originalStop();
    cleanupPreviewVideo(video);
    for (const track of source.getTracks()) {
      if (track.readyState !== 'ended') {
        track.stop();
      }
    }
  };

  outputTrack.stop = stopMirroredStream;
  outputTrack.addEventListener('ended', stopMirroredStream, { once: true });
  sourceTrack.addEventListener('ended', stopMirroredStream, { once: true });
  try {
    outputTrack.contentHint = sourceTrack.contentHint || 'motion';
  } catch {
    // Firefox 和部分 WebView 不支持 contentHint。
  }

  mirrorPlaybackByStream.set(output, {
    pause: () => {
      if (stopped || sourceStopped || video.paused) {
        return;
      }
      pausedForAudioCapture = true;
      try {
        video.pause();
      } catch {
        // jsdom 和部分 WebView 的 pause 会抛未实现错误。
      }
    },
    resume: () => {
      if (stopped || sourceStopped || !pausedForAudioCapture) {
        return;
      }
      pausedForAudioCapture = false;
      resumeMirror();
    },
    stopSource: () => {
      if (stopped || sourceStopped) {
        return;
      }
      sourceStopped = true;
      pausedForAudioCapture = true;
      stopTick();
      try {
        video.pause();
      } catch {
        // jsdom 和部分 WebView 的 pause 会抛未实现错误。
      }
      video.srcObject = null;
      for (const track of source.getTracks()) {
        if (track.readyState !== 'ended') {
          track.stop();
        }
      }
    },
  });

  return output;
};
